import React, { useState, useEffect } from 'react';
import { cajaAPI } from '../api/client';
import { Wallet, Plus, DollarSign, TrendingUp, TrendingDown, LogOut, ArrowDownCircle, ArrowUpCircle, ShoppingCart, FileText, MessageCircle } from 'lucide-react';
import { useConfig } from '../context/ConfigContext';
import { useAuth } from '../context/AuthContext';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const CajaPage = () => {
  const [cajaActual, setCajaActual] = useState(null);
  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAbrirModal, setShowAbrirModal] = useState(false);
  const [showCerrarModal, setShowCerrarModal] = useState(false);
  const [showIngresoModal, setShowIngresoModal] = useState(false);
  const [showRetiroModal, setShowRetiroModal] = useState(false);
  const [montoInicial, setMontoInicial] = useState('');
  const [montoCierre, setMontoCierre] = useState('');
  const [monto, setMonto] = useState('');
  const [concepto, setConcepto] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [resumenCierre, setResumenCierre] = useState(null);
  const { formatCurrency, formatDateTime, config } = useConfig();
  const { user } = useAuth();

  useEffect(() => {
    loadCajaActual();
  }, []);

  const loadCajaActual = async () => {
    try {
      const response = await cajaAPI.getActual();
      if (response.data.success) {
        setCajaActual(response.data.data);
        if (response.data.data) {
          loadMovimientos(response.data.data.id);
        }
      } else {
        console.error('Error success=false:', response.data.message);
      }
    } catch (error) {
      console.error('Error al cargar caja:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadMovimientos = async (cajaId) => {
    try {
      const response = await cajaAPI.getMovimientos(cajaId);
      if (response.data.success) {
        setMovimientos(response.data.data || []);
      } else {
        console.error('Error success=false:', response.data.message);
      }
    } catch (error) {
      console.error('Error al cargar movimientos:', error);
      setError(error.message);
    }
  };

  const abrirCaja = async () => {
    if (!montoInicial || parseFloat(montoInicial) < 0) {
      alert('❌ Por favor ingrese un monto válido');
      return;
    }

    const datos = {
      monto_inicial: parseFloat(montoInicial),
      usuario_id: user?.id || 1
    };

    console.log('Abriendo caja con datos:', datos);

    try {
      const response = await cajaAPI.abrir(datos);
      console.log('Respuesta al abrir caja:', response.data);

      if (response.data.success) {
        alert('✅ Caja abierta exitosamente');
        setShowAbrirModal(false);
        setMontoInicial('');
        setObservaciones('');
        loadCajaActual();
      } else {
        alert('❌ Error: ' + (response.data.message || 'Error al abrir caja'));
      }
    } catch (error) {
      console.error('Error completo al abrir caja:', error);
      console.error('Respuesta del servidor:', error.response?.data);
      
      // Si ya hay una caja abierta, recargar para mostrarla
      if (error.response?.data?.message?.includes('Ya existe una caja abierta')) {
        if (window.confirm('⚠️ Ya existe una caja abierta.\n\n¿Desea cargar la caja existente?')) {
          setShowAbrirModal(false);
          setMontoInicial('');
          setObservaciones('');
          loadCajaActual();
        }
      } else {
        alert('❌ Error al abrir caja: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const prepararCierreCaja = () => {
    if (!cajaActual) return;
    
    // Calcular resumen de cierre
    const montoInicial = parseFloat(cajaActual.monto_inicial) || 0;
    const totalIngresos = movimientos
      .filter(m => m.tipo === 'ingreso')
      .reduce((sum, m) => sum + parseFloat(m.monto), 0);
    const totalEgresos = movimientos
      .filter(m => m.tipo === 'egreso')
      .reduce((sum, m) => sum + parseFloat(m.monto), 0);
    
    const montoEsperado = montoInicial + totalIngresos - totalEgresos;
    
    setResumenCierre({
      montoInicial,
      totalIngresos,
      totalEgresos,
      montoEsperado,
      numeroMovimientos: movimientos.length
    });
    
    setMontoCierre(montoEsperado.toString());
    setShowCerrarModal(true);
  };

  const generarPDFCierre = (datosCierre) => {
    const doc = new jsPDF();
    const nombreNegocio = config?.nombre_negocio || 'Mi Negocio';
    const direccion = config?.direccion || '';
    const telefono = config?.telefono || '';
    
    // Header
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text(nombreNegocio.toUpperCase(), 105, 20, { align: 'center' });
    
    if (direccion || telefono) {
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      let infoY = 27;
      if (direccion) {
        doc.text(direccion, 105, infoY, { align: 'center' });
        infoY += 5;
      }
      if (telefono) {
        doc.text(`Tel: ${telefono}`, 105, infoY, { align: 'center' });
      }
    }
    
    // Título
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('CIERRE DE CAJA', 105, 45, { align: 'center' });
    
    // Línea separadora
    doc.setDrawColor(41, 128, 185);
    doc.setLineWidth(0.5);
    doc.line(20, 48, 190, 48);
    
    // Información del cierre
    doc.setFontSize(11);
    doc.setFont(undefined, 'normal');
    let yPos = 58;
    
    doc.text(`Fecha: ${formatDateTime(new Date())}`, 20, yPos);
    yPos += 7;
    doc.text(`Usuario: ${user?.username || 'Sistema'}`, 20, yPos);
    yPos += 7;
    doc.text(`Turno: ${formatDateTime(cajaActual.fecha_apertura)} - ${formatDateTime(new Date())}`, 20, yPos);
    
    yPos += 12;
    
    // Resumen financiero
    const tableData = [
      ['Concepto', 'Monto'],
      ['Monto Inicial', formatCurrency(datosCierre.montoInicial)],
      ['Total Ingresos (+)', formatCurrency(datosCierre.totalIngresos)],
      ['Total Egresos (-)', formatCurrency(datosCierre.totalEgresos)],
      ['', ''],
      ['MONTO ESPERADO', formatCurrency(datosCierre.montoEsperado)],
      ['MONTO CONTADO', formatCurrency(datosCierre.montoContado)],
      ['DIFERENCIA', formatCurrency(datosCierre.diferencia)]
    ];
    
    doc.autoTable({
      startY: yPos,
      head: [],
      body: tableData,
      theme: 'grid',
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 100 },
        1: { halign: 'right', cellWidth: 70 }
      },
      styles: { fontSize: 10 },
      didParseCell: function(data) {
        // Resaltar filas importantes
        if (data.row.index >= 5) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [240, 240, 240];
        }
        if (data.row.index === 7) { // Diferencia
          if (datosCierre.diferencia > 0) {
            data.cell.styles.textColor = [0, 128, 0]; // Verde
          } else if (datosCierre.diferencia < 0) {
            data.cell.styles.textColor = [255, 0, 0]; // Rojo
          }
        }
      }
    });
    
    yPos = doc.lastAutoTable.finalY + 10;
    
    // Detalle de movimientos si hay espacio
    if (yPos < 200 && movimientos.length > 0) {
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('DETALLE DE MOVIMIENTOS', 20, yPos);
      yPos += 8;
      
      const movimientosData = movimientos.slice(0, 10).map(mov => [
        formatDateTime(mov.fecha),
        mov.tipo === 'ingreso' ? 'Ingreso' : 'Egreso',
        mov.concepto,
        (mov.tipo === 'ingreso' ? '+' : '-') + formatCurrency(mov.monto)
      ]);
      
      doc.autoTable({
        startY: yPos,
        head: [['Fecha/Hora', 'Tipo', 'Concepto', 'Monto']],
        body: movimientosData,
        theme: 'striped',
        headStyles: { fillColor: [41, 128, 185] },
        columnStyles: {
          0: { cellWidth: 45 },
          1: { cellWidth: 25 },
          2: { cellWidth: 70 },
          3: { halign: 'right', cellWidth: 40 }
        },
        styles: { fontSize: 9 }
      });
      
      if (movimientos.length > 10) {
        doc.setFontSize(9);
        doc.setFont(undefined, 'italic');
        doc.text(`... y ${movimientos.length - 10} movimientos más`, 20, doc.lastAutoTable.finalY + 5);
      }
    }
    
    // Estado del cierre
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    let estadoTexto = '';
    let estadoColor = [0, 0, 0];
    
    if (Math.abs(datosCierre.diferencia) < 0.01) {
      estadoTexto = '✓ CUADRE PERFECTO';
      estadoColor = [0, 128, 0];
    } else if (datosCierre.diferencia > 0) {
      estadoTexto = `⚠ SOBRANTE: ${formatCurrency(datosCierre.diferencia)}`;
      estadoColor = [255, 140, 0];
    } else {
      estadoTexto = `⚠ FALTANTE: ${formatCurrency(Math.abs(datosCierre.diferencia))}`;
      estadoColor = [255, 0, 0];
    }
    
    doc.setTextColor(...estadoColor);
    doc.text(estadoTexto, 105, 270, { align: 'center' });
    
    // Footer
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(8);
    doc.setFont(undefined, 'italic');
    doc.text(`Generado: ${new Date().toLocaleString('es-GT')}`, 105, 285, { align: 'center' });
    
    return doc;
  };

  const enviarWhatsAppCierre = (datosCierre) => {
    const nombreNegocio = config?.nombre_negocio || 'Nuestro Negocio';
    
    let mensaje = `*${nombreNegocio.toUpperCase()}*\n`;
    mensaje += `📊 *CIERRE DE CAJA*\n`;
    mensaje += `━━━━━━━━━━━━━━━━\n\n`;
    mensaje += `📅 *Fecha:* ${formatDateTime(new Date())}\n`;
    mensaje += `👤 *Usuario:* ${user?.username || 'Sistema'}\n\n`;
    
    mensaje += `*RESUMEN DEL TURNO:*\n`;
    mensaje += `• Monto Inicial: ${formatCurrency(datosCierre.montoInicial)}\n`;
    mensaje += `• Total Ingresos: ${formatCurrency(datosCierre.totalIngresos)}\n`;
    mensaje += `• Total Egresos: ${formatCurrency(datosCierre.totalEgresos)}\n`;
    mensaje += `━━━━━━━━━━━━━━━━\n`;
    mensaje += `💰 *Monto Esperado:* ${formatCurrency(datosCierre.montoEsperado)}\n`;
    mensaje += `🔍 *Monto Contado:* ${formatCurrency(datosCierre.montoContado)}\n`;
    mensaje += `📋 *Diferencia:* ${formatCurrency(datosCierre.diferencia)}\n\n`;
    
    if (Math.abs(datosCierre.diferencia) < 0.01) {
      mensaje += `✅ *Estado:* CUADRE PERFECTO\n`;
    } else if (datosCierre.diferencia > 0) {
      mensaje += `🟡 *Estado:* SOBRANTE\n`;
    } else {
      mensaje += `🔴 *Estado:* FALTANTE\n`;
    }
    
    mensaje += `\n📈 *Total Movimientos:* ${datosCierre.numeroMovimientos}`;
    
    if (datosCierre.observaciones) {
      mensaje += `\n\n📝 *Observaciones:* ${datosCierre.observaciones}`;
    }
    
    const url = `https://wa.me/?text=${encodeURIComponent(mensaje)}`;
    window.open(url, '_blank');
  };

  const cerrarCaja = async () => {
    if (!montoCierre || parseFloat(montoCierre) < 0) {
      alert('❌ Por favor ingrese un monto válido');
      return;
    }

    try {
      const response = await cajaAPI.cerrar(cajaActual.id, {
        monto_final: parseFloat(montoCierre),
        notas: observaciones || 'Cierre de caja'
      });

      if (response.data.success) {
        // Datos para el PDF y WhatsApp
        const diferencia = parseFloat(montoCierre) - resumenCierre.montoEsperado;
        const datosCierre = {
          montoInicial: resumenCierre.montoInicial,
          totalIngresos: resumenCierre.totalIngresos,
          totalEgresos: resumenCierre.totalEgresos,
          montoEsperado: resumenCierre.montoEsperado,
          montoContado: parseFloat(montoCierre),
          diferencia: diferencia,
          numeroMovimientos: resumenCierre.numeroMovimientos,
          observaciones: observaciones
        };
        
        // Preguntar qué hacer con el cierre
        const opciones = window.confirm(
          `✅ CIERRE DE CAJA EXITOSO\n\n` +
          `Diferencia: ${formatCurrency(diferencia)}\n` +
          `Estado: ${diferencia === 0 ? 'Cuadre Perfecto' : diferencia > 0 ? 'Sobrante' : 'Faltante'}\n\n` +
          `¿Desea generar el reporte PDF del cierre?`
        );
        
        if (opciones) {
          const doc = generarPDFCierre(datosCierre);
          doc.save(`cierre_caja_${new Date().getTime()}.pdf`);
          
          // Preguntar si enviar por WhatsApp
          if (window.confirm('¿Desea enviar el reporte por WhatsApp?')) {
            enviarWhatsAppCierre(datosCierre);
          }
        }
        
        setShowCerrarModal(false);
        setMontoCierre('');
        setObservaciones('');
        setResumenCierre(null);
        loadCajaActual();
      } else {
        alert('❌ Error: ' + (response.data.message || 'Error al cerrar caja'));
      }
    } catch (error) {
      console.error('Error al cerrar caja:', error);
      alert('❌ Error al cerrar caja: ' + (error.response?.data?.message || error.message));
    }
  };

  const registrarIngreso = async () => {
    if (!monto || parseFloat(monto) <= 0) {
      alert('❌ Por favor ingrese un monto válido');
      return;
    }
    if (!concepto.trim()) {
      alert('❌ Por favor ingrese un concepto');
      return;
    }

    try {
      const response = await cajaAPI.registrarMovimiento({
        caja_id: cajaActual.id,
        tipo: 'ingreso',
        monto: parseFloat(monto),
        concepto: concepto,
        referencia: observaciones || ''
      });

      if (response.data.success) {
        alert('✅ Ingreso registrado exitosamente');
        setShowIngresoModal(false);
        setMonto('');
        setConcepto('');
        setObservaciones('');
        loadMovimientos(cajaActual.id);
      } else {
        alert('❌ Error: ' + (response.data.message || 'Error al registrar ingreso'));
      }
    } catch (error) {
      console.error('Error al registrar ingreso:', error);
      alert('❌ Error al registrar ingreso: ' + (error.response?.data?.message || error.message));
    }
  };

  const registrarRetiro = async () => {
    if (!monto || parseFloat(monto) <= 0) {
      alert('❌ Por favor ingrese un monto válido');
      return;
    }
    if (!concepto.trim()) {
      alert('❌ Por favor ingrese un concepto');
      return;
    }

    try {
      const response = await cajaAPI.registrarMovimiento({
        caja_id: cajaActual.id,
        tipo: 'egreso',
        monto: parseFloat(monto),
        concepto: concepto,
        referencia: observaciones || ''
      });

      if (response.data.success) {
        alert('✅ Retiro registrado exitosamente');
        setShowRetiroModal(false);
        setMonto('');
        setConcepto('');
        setObservaciones('');
        loadMovimientos(cajaActual.id);
      } else {
        alert('❌ Error: ' + (response.data.message || 'Error al registrar retiro'));
      }
    } catch (error) {
      console.error('Error al registrar retiro:', error);
      alert('❌ Error al registrar retiro: ' + (error.response?.data?.message || error.message));
    }
  };

  const calcularTotales = () => {
    if (!movimientos) return { ingresos: 0, egresos: 0, balance: 0 };
    
    const ingresos = movimientos
      .filter(m => m.tipo === 'ingreso')
      .reduce((sum, m) => sum + (parseFloat(m.monto) || 0), 0);
      
    const egresos = movimientos
      .filter(m => m.tipo === 'egreso')
      .reduce((sum, m) => sum + (parseFloat(m.monto) || 0), 0);
      
    const montoActual = cajaActual 
      ? (parseFloat(cajaActual.monto_inicial) || 0) + ingresos - egresos
      : 0;

    return { 
      ingresos, 
      egresos, 
      balance: montoActual
    };
  };

  const { ingresos, egresos, balance } = calcularTotales();

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      )}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Caja</h1>
        <div className="flex gap-2">
          {!cajaActual ? (
            <button 
              onClick={() => setShowAbrirModal(true)}
              className="btn-primary flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Abrir Caja
            </button>
          ) : (
            <>
              <button 
                onClick={() => window.location.href = '/pos'}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
              >
                <ShoppingCart className="w-5 h-5" />
                Venta Rápida
              </button>
              <button 
                onClick={prepararCierreCaja}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <LogOut className="w-5 h-5" />
                Cerrar Turno
              </button>
              <button 
                onClick={() => setShowIngresoModal(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <ArrowDownCircle className="w-5 h-5" />
                Ingresar
              </button>
              <button 
                onClick={() => setShowRetiroModal(true)}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2"
              >
                <ArrowUpCircle className="w-5 h-5" />
                Retirar
              </button>
              <button 
                onClick={() => setShowCerrarModal(true)}
                className="btn-danger flex items-center gap-2"
              >
                <LogOut className="w-5 h-5" />
                Cerrar Caja
              </button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="card text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-500 text-lg">Cargando información de caja...</p>
        </div>
      ) : !cajaActual ? (
        <div className="card text-center py-16 bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl">
          <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
            <Wallet className="w-10 h-10 text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-700 mb-2">La Caja está Cerrada</h2>
          <p className="text-gray-500 mb-8 max-w-md mx-auto">
            No hay ninguna caja abierta en este momento. Para comenzar a realizar ventas y movimientos, debes abrir una nueva caja.
          </p>
          <button 
            onClick={() => setShowAbrirModal(true)}
            className="px-8 py-3 bg-primary-600 text-white text-lg rounded-lg hover:bg-primary-700 transition-colors shadow-lg flex items-center gap-2 mx-auto"
          >
            <Plus className="w-6 h-6" />
            Abrir Nueva Caja
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Monto Inicial</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(cajaActual?.monto_inicial || 0)}
                  </p>
                </div>
                <Wallet className="w-12 h-12 text-gray-600 opacity-20" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Ingresos</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(ingresos || 0)}
                  </p>
                </div>
                <TrendingUp className="w-12 h-12 text-green-600 opacity-20" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Egresos</p>
                  <p className="text-2xl font-bold text-red-600">
                    {formatCurrency(egresos || 0)}
                  </p>
                </div>
                <TrendingDown className="w-12 h-12 text-red-600 opacity-20" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Balance Total</p>
                  <p className="text-2xl font-bold text-primary-600">
                    {formatCurrency(balance || 0)}
                  </p>
                </div>
                <DollarSign className="w-12 h-12 text-primary-600 opacity-20" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Por Cobrar (Créditos)</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {formatCurrency(cajaActual.total_por_cobrar || 0)}
                  </p>
                </div>
                <TrendingUp className="w-12 h-12 text-orange-600 opacity-20" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4">Movimientos de Caja</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Concepto</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {!movimientos || movimientos.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                        No hay movimientos registrados
                      </td>
                    </tr>
                  ) : (
                    movimientos.map((mov, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          {mov.fecha ? formatDateTime(mov.fecha) : '-'}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded ${
                            mov.tipo === 'ingreso' 
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {mov.tipo === 'ingreso' ? 'Ingreso' : 'Egreso'}
                          </span>
                        </td>
                        <td className="px-6 py-4">{mov.concepto || '-'}</td>
                        <td className="px-6 py-4 font-bold">
                          <span className={mov.tipo === 'ingreso' ? 'text-green-600' : 'text-red-600'}>
                            {mov.tipo === 'ingreso' ? '+' : '-'}{formatCurrency(mov.monto || 0)}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Modal Abrir Caja */}
      {showAbrirModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Plus className="w-6 h-6 text-green-600" />
              Abrir Caja
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monto Inicial <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={montoInicial}
                  onChange={(e) => setMontoInicial(e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  autoFocus
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  💡 <strong>Tip:</strong> Cuenta el efectivo disponible antes de abrir la caja.
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAbrirModal(false);
                  setMontoInicial('');
                  setObservaciones('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={abrirCaja}
                disabled={!montoInicial}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Abrir Caja
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Cerrar Caja */}
      {showCerrarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full flex flex-col max-h-[90vh]">
            <div className="p-6 border-b">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <LogOut className="w-6 h-6 text-red-600" />
                Cerrar Caja
              </h2>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              {/* Resumen de Caja */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Monto Inicial:</span>
                  <span className="font-medium">{formatCurrency(cajaActual?.monto_inicial || 0)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Ingresos:</span>
                  <span className="font-medium text-green-600">+{formatCurrency(ingresos)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Egresos:</span>
                  <span className="font-medium text-red-600">-{formatCurrency(egresos)}</span>
                </div>
                <div className="border-t pt-2 flex justify-between">
                  <span className="font-semibold">Balance Esperado:</span>
                  <span className="font-bold text-primary-600">{formatCurrency(balance)}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monto de Cierre Real <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={montoCierre}
                  onChange={(e) => setMontoCierre(e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  autoFocus
                />
                {montoCierre && (
                  <p className={`text-sm mt-1 ${
                    Math.abs(parseFloat(montoCierre) - balance) < 0.01
                      ? 'text-green-600'
                      : 'text-orange-600'
                  }`}>
                    {Math.abs(parseFloat(montoCierre) - balance) < 0.01
                      ? '✓ El monto coincide con el balance'
                      : `⚠ Diferencia: ${formatCurrency(Math.abs(parseFloat(montoCierre) - balance))}`
                    }
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observaciones (Opcional)
                </label>
                <textarea
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  placeholder="Notas sobre el cierre de caja..."
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  ⚠ <strong>Importante:</strong> Verifica que el monto físico coincida con el balance del sistema.
                </p>
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 rounded-b-lg">
              {/* Botones de acción rápida */}
              {resumenCierre && (
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => {
                      const datosCierre = {
                        montoInicial: resumenCierre.montoInicial,
                        totalIngresos: resumenCierre.totalIngresos,
                        totalEgresos: resumenCierre.totalEgresos,
                        montoEsperado: resumenCierre.montoEsperado,
                        montoContado: parseFloat(montoCierre) || resumenCierre.montoEsperado,
                        diferencia: (parseFloat(montoCierre) || resumenCierre.montoEsperado) - resumenCierre.montoEsperado,
                        numeroMovimientos: resumenCierre.numeroMovimientos,
                        observaciones: observaciones
                      };
                      const doc = generarPDFCierre(datosCierre);
                      doc.save(`cierre_caja_${new Date().getTime()}.pdf`);
                    }}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    <FileText className="w-4 h-4" />
                    Vista Previa PDF
                  </button>
                  <button
                    onClick={() => {
                      const datosCierre = {
                        montoInicial: resumenCierre.montoInicial,
                        totalIngresos: resumenCierre.totalIngresos,
                        totalEgresos: resumenCierre.totalEgresos,
                        montoEsperado: resumenCierre.montoEsperado,
                        montoContado: parseFloat(montoCierre) || resumenCierre.montoEsperado,
                        diferencia: (parseFloat(montoCierre) || resumenCierre.montoEsperado) - resumenCierre.montoEsperado,
                        numeroMovimientos: resumenCierre.numeroMovimientos,
                        observaciones: observaciones
                      };
                      enviarWhatsAppCierre(datosCierre);
                    }}
                    className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                  >
                    <MessageCircle className="w-4 h-4" />
                    WhatsApp
                  </button>
                </div>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowCerrarModal(false);
                    setMontoCierre('');
                    setObservaciones('');
                    setResumenCierre(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-white transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={cerrarCaja}
                  disabled={!montoCierre}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Cerrar Caja
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ingresar Efectivo */}
      {showIngresoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <ArrowDownCircle className="w-6 h-6 text-green-600" />
              Ingresar Efectivo
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monto <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={monto}
                  onChange={(e) => setMonto(e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Concepto <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={concepto}
                  onChange={(e) => setConcepto(e.target.value)}
                  placeholder="Ej: Pago de cliente, Préstamo, etc."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observaciones (Opcional)
                </label>
                <textarea
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  placeholder="Detalles adicionales..."
                  rows="2"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-800">
                  💵 Este ingreso se sumará al balance total de la caja.
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowIngresoModal(false);
                  setMonto('');
                  setConcepto('');
                  setObservaciones('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={registrarIngreso}
                disabled={!monto || !concepto}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Registrar Ingreso
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Retirar Efectivo */}
      {showRetiroModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <ArrowUpCircle className="w-6 h-6 text-orange-600" />
              Retirar Efectivo
            </h2>
            
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Balance Actual:</span>
                  <span className="font-bold text-primary-600">{formatCurrency(balance)}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monto a Retirar <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={monto}
                  onChange={(e) => setMonto(e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  max={balance}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  autoFocus
                />
                {monto && parseFloat(monto) > balance && (
                  <p className="text-sm text-red-600 mt-1">
                    ⚠ El monto excede el balance disponible
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Concepto <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={concepto}
                  onChange={(e) => setConcepto(e.target.value)}
                  placeholder="Ej: Pago a proveedor, Gastos, etc."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observaciones (Opcional)
                </label>
                <textarea
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  placeholder="Detalles adicionales..."
                  rows="2"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <p className="text-sm text-orange-800">
                  ⚠ Este retiro se restará del balance total de la caja.
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowRetiroModal(false);
                  setMonto('');
                  setConcepto('');
                  setObservaciones('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={registrarRetiro}
                disabled={!monto || !concepto || parseFloat(monto) > balance}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Registrar Retiro
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CajaPage;
